import UserModel from '@/_models/UserModel'
import BeaconModel from '@/_models/BeaconModel'
import { rotate } from '@/_utils/math'
import { prettyPinConfig } from '@/_utils/pretty-pin'
import { isCardOff, isValidCoord } from '@/_utils/card'
import CardModel from '@/_models/CardModel'

export class MapFixedDevice {
  private vm: any
  private google: any
  private map: any
  private bounds: any
  private fixedDevice!: BeaconModel | CardModel
  private nearestUsers!: UserModel[]
  private usersAtFixedDevice!: string
  private revertAngle: number = 0

  constructor(vm) {
    this.vm = vm
    this.google = vm.google
    this.map = vm.map
    this.bounds = vm.bounds
  }

  get fixedDeviceLatLon(): any {
    const orgLatLng = new this.vm.google.maps.LatLng(this.fixedDevice.lat, this.fixedDevice.lon)
    if (typeof this.revertAngle === 'undefined' || this.revertAngle === 0 || !this.map) {
      return orgLatLng
    }

    const project = this.map.getProjection()
    if (!project) {
      return orgLatLng
    }

    const orgPoint = project.fromLatLngToPoint(orgLatLng)
    const orgCenter = project.fromLatLngToPoint(this.map.getCenter())
    const newp = rotate(orgPoint.x, orgPoint.y, orgCenter.x, orgCenter.y, this.revertAngle * -1)

    return project.fromPointToLatLng(new this.google.maps.Point(newp[0], newp[1]))
  }

  get canEnableCamera(): boolean {
    return !!(this.vm.manageFeature && this.vm.manageFeature.ip_camera && this.fixedDevice.camera_address)
  }

  get floorFixedId() {
    if (this.vm.widgetOptions && this.vm.widgetOptions.floor === 'all') {
      return ''
    }

    return this.fixedDevice.floor_id
  }

  public setRevertAngle(angle: number) {
    this.revertAngle = angle
  }

  public setFixedDevice(fixedDevice: BeaconModel | CardModel) {
    this.fixedDevice = fixedDevice
    this.nearestUsers = this.usersNearestFixedDevice()

    const coma = this.nearestUsers.length > 1 ? ', ' : '<br />'
    const prefixLabel = this.nearestUsers.length ? `<strong>User name:</strong> ` : ''
    this.usersAtFixedDevice =
      prefixLabel +
      this.nearestUsers
        .map(user => {
          return user.name
        })
        .join(coma)
  }

  public showMarkerFixedDevice() {
    if (!this.isValidFixedDevice()) {
      return
    }

    const vm = this.vm
    const existedMarker = this.existedMarkerBeacon()
    if (existedMarker) {
      const typeIcon = this.canEnableCamera ? 'beacon-camera' : 'beacon'

      existedMarker.users = this.nearestUsers
      existedMarker.setIcon(this.pinIconFixedDevice(typeIcon))

      if (this.usersAtFixedDevice || this.canEnableCamera) {
        existedMarker.infowindow = this.generateInforContent(vm)
      } else {
        existedMarker.infowindow = null
      }

      return
    } else {
      this.addFixedDevicesToMap()
    }
  }

  private addFixedDevicesToMap(): void {
    const vm = this.vm
    const typeIcon = this.canEnableCamera ? 'beacon-camera' : 'beacon'
    const marker = new vm.google.maps.Marker({
      position: this.fixedDeviceLatLon,
      map: vm.map,
      title: this.fixedDevice.location_name,
      icon: this.pinIconFixedDevice(typeIcon),
    })

    if (this.bounds && this.nearestUsers.length) {
      this.bounds.extend(this.fixedDeviceLatLon)
    }

    marker.fixed_id = this.fixedDevice._id
    marker.users = this.nearestUsers
    vm.pinMarkers.push(marker)

    const self = this

    this.clearEventPrevious(marker, ['click', 'mouseover'])

    vm.google.maps.event.addListener(marker, 'click', function(this: any) {
      if (this.infowindow) {
        self.closeAllInfowindow()
        this.infowindow.close()
      }

      vm.handlePinClick(marker)
    })

    if (this.canEnableCamera) {
      marker.infowindow = this.generateInforContent(this)
    }

    this.google.maps.event.addListener(marker, 'mouseover', function(this: any) {
      if (this.infowindow) {
        this.infowindow.close()
        self.closeAllInfowindow()
        this.infowindow.open(vm.map, this)
      }
    })

    // this.clearEventPrevious(vm.map, ['click'])
    this.google.maps.event.addListener(vm.map, 'click', function(this: any) {
      if (marker.infowindow) {
        marker.infowindow.close()
      }
    })
  }

  private generateInforContent(vm) {
    if (!this.fixedDevice.camera_address && !this.usersAtFixedDevice) {
      return null
    }

    let content = ''
    if (this.canEnableCamera) {
      content = `
        <div class="container is-fullhd has-text-centered mb-1">
          <img alt="${this.fixedDevice.location_name ||
            ''}" onerror="this.onerror=null;this.src='https://i.pinimg.com/originals/d0/f8/78/d0f878e457ee6254b07bfb928886941d.png'" src="${
        this.fixedDevice.camera_address
      }" style="width: 100%; height: 100%;"></img>
          <button class="button is-info" onclick="openCameraModal('${this.fixedDevice._id}', '${this.floorFixedId}');">
          ${this.vm.$t('button.open_video')}
          </button>
        </div>
      `
    }

    return new vm.google.maps.InfoWindow({
      content: content + this.usersAtFixedDevice,
      maxWidth: 200,
      pixelOffset: new vm.google.maps.Size(0, 20), // x-axis --- y-axis
    })
  }

  private clearEventPrevious(target, event: string[]) {
    event.forEach(evt => {
      this.google.maps.event.clearListeners(target, evt)
    })
  }

  private isValidFixedDevice(): boolean {
    return isValidCoord(this.fixedDevice.lat) && isValidCoord(this.fixedDevice.lon)
  }

  private pinIconFixedDevice(typeIcon?: string): string {
    let addtionClass = ''
    if (typeof this.vm.isHiddenFixedDevice !== 'undefined') {
      addtionClass = this.vm.isHiddenFixedDevice(this.fixedDevice) ? 'is-hidden' : ''
    }

    return prettyPinConfig({
      type: typeIcon ? typeIcon : 'beacon',
      isSelected: this.vm.isSelectedByUsersAndCheckedRow(this.nearestUsers),
      nearestUsers: this.nearestUsers,
      addtionClass,
      userType: '',
      uniqueId: this.fixedDevice._id,
    })
  }

  private usersNearestFixedDevice(): UserModel[] {
    if (typeof this.vm.usersNearestFixedDevice !== 'undefined') {
      return this.vm.usersNearestFixedDevice(this.fixedDevice)
    }

    if (!this.vm.users.length) {
      return []
    }

    const users: UserModel[] = []
    for (const user of this.vm.filteredUsers) {
      const isNearest = user.nearest_beacon_id === this.fixedDevice._id || user.nearest_card_id === this.fixedDevice._id
      if (!isCardOff(user) && isNearest) {
        users.push(user)
      }
    }

    return users
  }

  private closeAllInfowindow() {
    const infowindows = document.getElementsByClassName('gm-style-iw-a')
    if (infowindows && infowindows.length) {
      for (const infowindow of infowindows) {
        if (infowindow && infowindow.parentNode) {
          infowindow.parentNode.removeChild(infowindow)
        }
      }
    }
  }

  private existedMarkerBeacon(): any {
    for (const marker of this.vm.pinMarkers) {
      if (marker.fixed_id === this.fixedDevice._id) {
        return marker
      }
    }

    return false
  }
}
